import React, { useEffect, useState } from "react";

import { FormattedMessage, IntlShape } from "react-intl";
import moment from "moment";
import DatePickerGroup from "../../../witadmin/CRUD/Common/DatePickerGroup";
import { WEEK_DAYS } from "../../../witadmin/Common/constants";
import FieldErrorList from "../../../witadmin/CRUD/Common/FieldErrorList";
import { messages } from "./messages";
import {
  Container,
  DatePickerContainer,
  DatePickerGroupStyled,
  WeekdayPickerContainer,
  Message,
  MessageTitle,
} from "./styles";
import "react-datepicker/dist/react-datepicker.css";
import "react-bootstrap-timezone-picker/dist/react-bootstrap-timezone-picker.min.css";
import { InputMultiCheckbox } from "../InputMultiCheckbox";
import { Button } from "react-bootstrap";

const restrictionTranslations = {
  TITLE: <FormattedMessage {...messages.bookingDate} />,
  START: <FormattedMessage {...messages.startBookable} />,
  END: <FormattedMessage {...messages.endBookable} />,
};

type RangeDateType = {
  id: string;
  startDate: string | null;
  endDate: string | null;
  weekDays: string[];
};

const defaultRangeWindow = {
  id: "",
  startDate: "",
  endDate: "",
  weekDays: [],
};

type OpenBookingProps = {
  field: Record<string, any>;
  intl: IntlShape;
  disabled: boolean;
  defaultValues: RangeDateType;
  onSave: (value: RangeDateType) => void;
};

export const OpenBooking = ({ field, intl, disabled, defaultValues, onSave }: OpenBookingProps) => {
  const [rangeWindow, setRangeWindow] = useState<RangeDateType>(defaultRangeWindow);

  useEffect(() => {
    setRangeWindow(defaultValues);
  }, [defaultValues]);

  const handleOnChange = (values: any) => {
    const result = { ...rangeWindow, weekDays: [...values] };
    setRangeWindow(result);
  };

  const handleOnChangeStartDate = (name: string) => ({ value }: { value: string }) => {
    const result = { ...rangeWindow, [name]: value };
    setRangeWindow(result);
  };

  const hasErrors = rangeWindow &&
    rangeWindow.endDate &&
    (moment(rangeWindow.endDate).isBefore(rangeWindow.startDate) ||
      moment(rangeWindow.endDate).isBefore(moment(new Date()).format("YYYY-MM-DD"))) && [
      intl.formatMessage(messages.endDateAfterStart),
    ];

  return (
    <Container>
      <div>
        <DatePickerContainer>
          <div></div>
          <label>{restrictionTranslations.START}</label>
          <DatePickerGroup
            disabled={disabled}
            value={rangeWindow && rangeWindow.startDate}
            field={{
              ...field,
              fieldName: "startDate",
              displayName: restrictionTranslations.START,
              selectsStart: true,
            }}
            formValues={{
              startDate: rangeWindow && rangeWindow.startDate,
              endDate: rangeWindow && rangeWindow.endDate,
            }}
            onChange={handleOnChangeStartDate("startDate")}
          />

          <DatePickerGroupStyled restrictionType="OPEN_BOOKING">
            <label>{restrictionTranslations.END}</label>
            <DatePickerGroup
              disabled={disabled}
              value={rangeWindow && rangeWindow.endDate}
              field={{
                ...field,
                fieldName: "endDate",
                displayName: restrictionTranslations.END,
                selectsEnd: true,
              }}
              formValues={{
                startDate: rangeWindow && rangeWindow.startDate,
                endDate: rangeWindow && rangeWindow.endDate,
              }}
              onChange={handleOnChangeStartDate("endDate")}
            />
            <FieldErrorList errors={hasErrors} />
          </DatePickerGroupStyled>

          <WeekdayPickerContainer>
            <Message>
              <MessageTitle>
                <FormattedMessage {...messages.weekDays} />
              </MessageTitle>

              <FormattedMessage {...messages.weekDaysDescription} />
            </Message>
            <InputMultiCheckbox
              defaultValues={rangeWindow.weekDays}
              className="witadmin-bs3"
              value={rangeWindow.weekDays}
              onChange={handleOnChange}
              field={{
                options:
                  [
                    {
                      ticker: WEEK_DAYS.MONDAY,
                      name: <FormattedMessage {...messages.monday} />,
                    },
                    {
                      ticker: WEEK_DAYS.TUESDAY,
                      name: <FormattedMessage {...messages.tuesday} />,
                    },
                    {
                      ticker: WEEK_DAYS.WEDNESDAY,
                      name: <FormattedMessage {...messages.wednesday} />,
                    },
                    {
                      ticker: WEEK_DAYS.THURSDAY,
                      name: <FormattedMessage {...messages.thursday} />,
                    },
                    {
                      ticker: WEEK_DAYS.FRIDAY,
                      name: <FormattedMessage {...messages.friday} />,
                    },
                    {
                      ticker: WEEK_DAYS.SATURDAY,
                      name: <FormattedMessage {...messages.saturday} />,
                    },
                    {
                      ticker: WEEK_DAYS.SUNDAY,
                      name: <FormattedMessage {...messages.sunday} />,
                    },
                  ] || [],
                selectables: true,
              }}
            />
          </WeekdayPickerContainer>
          <Button
            variant="primary"
            className="mt-3 mr-3"
            disabled={
              !rangeWindow.startDate ||
              !rangeWindow.endDate ||
              !!hasErrors ||
              !rangeWindow.weekDays.length
            }
            onClick={() => {
              onSave(rangeWindow);
            }}>
            <FormattedMessage {...messages.save} />
          </Button>
        </DatePickerContainer>
      </div>
    </Container>
  );
};
